import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { api, useAuth } from '../context/AuthContext';

const feld = 'border border-gray-200 px-3 py-2 text-sm focus:border-[#b8893d] focus:outline-none';

export const ZweiFaktor = () => {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const aktiv = !!user?.zwei_fa_aktiv;
  const run = async (fn) => {
    setBusy(true); setMsg(null);
    try { await fn(); } catch (e) { setMsg({ ok: false, text: e?.response?.data?.detail || 'Fehler' }); } finally { setBusy(false); }
  };
  const starten = () => run(async () => { const { data } = await api.post('/auth/2fa/setup'); setSetup(data); setCode(''); });
  const aktivieren = () => run(async () => {
    await api.post('/auth/2fa/enable', { code }); setSetup(null); setCode('');
    setUser((u) => ({ ...u, zwei_fa_aktiv: true })); setMsg({ ok: true, text: t('konto.zweifa_an') });
  });
  const deaktivieren = () => run(async () => {
    await api.post('/auth/2fa/disable', { code, password: pw }); setCode(''); setPw('');
    setUser((u) => ({ ...u, zwei_fa_aktiv: false })); setMsg({ ok: true, text: t('konto.zweifa_aus') });
  });
  return (
    <div className="space-y-3" data-testid="zweifa-section">
      <p className="text-sm text-[#34536b] flex items-center gap-2"><ShieldCheck className={`w-4 h-4 ${aktiv ? 'text-emerald-600' : 'text-[#7a8a99]'}`} />
        <span data-testid="zweifa-status">{aktiv ? t('konto.zweifa_status_an') : t('konto.zweifa_status_aus')}</span></p>
      {!aktiv && !setup && <button type="button" onClick={starten} disabled={busy} className="px-4 py-2 text-xs tracking-wider border border-[#b8893d] text-[#1B3A5C] rounded-sm hover:bg-[#fbf7f0]" data-testid="zweifa-setup">{t('konto.zweifa_einrichten')}</button>}
      {setup && (
        <div className="flex flex-wrap gap-4 items-start">
          <img src={setup.qr} alt="QR" className="w-40 h-40 border border-gray-200" data-testid="zweifa-qr" />
          <div className="space-y-2 text-xs text-[#34536b] max-w-xs">
            <p>{t('konto.zweifa_scan')}</p>
            <p className="font-mono break-all bg-gray-50 p-2" data-testid="zweifa-secret">{setup.secret}</p>
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" inputMode="numeric" className={`${feld} font-mono w-32`} data-testid="zweifa-code" />
            <button type="button" onClick={aktivieren} disabled={busy || code.length !== 6} className="ml-2 px-4 py-2 bg-[#1B3A5C] text-white rounded-sm disabled:opacity-50" data-testid="zweifa-enable">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : t('konto.zweifa_bestaetigen')}</button>
          </div>
        </div>
      )}
      {aktiv && (
        <div className="flex flex-wrap gap-2 items-center">
          {user?.auth_provider !== 'google' && <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t('konto.passwort')} className={feld} data-testid="zweifa-disable-pw" />}
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" inputMode="numeric" className={`${feld} font-mono w-28`} data-testid="zweifa-disable-code" />
          <button type="button" onClick={deaktivieren} disabled={busy || code.length !== 6} className="px-4 py-2 text-xs border border-rose-300 text-rose-700 rounded-sm disabled:opacity-50" data-testid="zweifa-disable">{t('konto.zweifa_deaktivieren')}</button>
        </div>
      )}
      {msg && <p className={`text-xs ${msg.ok ? 'text-emerald-700' : 'text-rose-700'}`} data-testid="zweifa-msg">{msg.text}</p>}
      {aktiv && <GemerkteGeraete />}
    </div>
  );
};

const GemerkteGeraete = () => {
  const [items, setItems] = useState(null);
  const [msg, setMsg] = useState('');
  const load = () => api.get('/auth/2fa/geraete').then((r) => setItems(r.data.items)).catch(() => setItems([]));
  React.useEffect(() => { load(); }, []);
  const widerrufen = async () => { const r = await api.delete('/auth/2fa/geraete'); setMsg(`${r.data.widerrufen} Gerät(e) abgemeldet – beim nächsten Login wird wieder der Code abgefragt.`); load(); };
  if (!items) return null;
  return (
    <div className="pt-3 border-t border-gray-100 space-y-2" data-testid="zweifa-geraete">
      <p className="text-xs text-[#34536b]">Für heute gemerkte Geräte (ohne Code bis Mitternacht):</p>
      {items.length ? <ul className="text-xs text-[#1B3A5C] space-y-1">{items.map((g) => <li key={g.id} data-testid={`zweifa-geraet-${g.id}`}>{(g.geraet || 'Unbekanntes Gerät').slice(0, 70)}{g.aktuell ? ' · dieses Gerät' : ''} · seit {new Date(g.erstellt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr</li>)}</ul>
        : <p className="text-xs text-gray-400" data-testid="zweifa-geraete-leer">Keine gemerkten Geräte.</p>}
      {items.length > 0 && <button type="button" onClick={widerrufen} className="px-3 py-1.5 text-xs border border-rose-300 text-rose-700 rounded-sm" data-testid="zweifa-geraete-widerrufen">Alle gemerkten Geräte abmelden</button>}
      {msg && <p className="text-xs text-emerald-700" data-testid="zweifa-geraete-msg">{msg}</p>}
    </div>
  );
};
